export type PcmPlaybackStats = {
  ttsFirstByteReceivedMs: number | null;
  audiblePlaybackStartedMs: number | null;
  playbackFinishedMs: number | null;
  prerollSamples: number;
  bufferDepthSamples: number;
  underrunCount: number;
  underrunDurationMs: number;
  overflowCount: number;
  fatalOverflow: boolean;
  playedMs: number;
};

type PlayerCallbacks = {
  onAudibleStart?: () => void;
  onEnded?: (stats: PcmPlaybackStats) => void;
  onFatalOverflow?: (stats: PcmPlaybackStats) => void;
  onError?: (message: string) => void;
};

const DEFAULT_PREROLL_MS = 80;
const MAX_PREROLL_MS = 200;
const PREROLL_STEP_MS = 16;

export class PcmPlaybackPlayer {
  private node: AudioWorkletNode | null = null;
  private loadPromise: Promise<void> | null = null;
  private odd: Uint8Array | null = null;
  private playing = false;
  /** After fatal_overflow, ignore PCM until beginUtterance (next bot_speech). */
  private aborted = false;
  private inputRate = 16000;
  private prerollMs = DEFAULT_PREROLL_MS;
  private utteranceStart = 0;
  private firstByteAt: number | null = null;
  private audibleAt: number | null = null;
  private finishedAt: number | null = null;
  private bufferDepth = 0;
  private underrunCount = 0;
  private underrunFrames = 0;
  private overflowCount = 0;
  private playedInputSamples = 0;
  private pending: Float32Array[] = [];
  private gain: GainNode | null = null;
  private muted = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly callbacks: PlayerCallbacks = {},
    inputSampleRate = 16000
  ) {
    if (inputSampleRate > 0) this.inputRate = inputSampleRate;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get isAborted(): boolean {
    return this.aborted;
  }

  getPlayedMs(): number {
    return Math.round((this.playedInputSamples / this.inputRate) * 1000);
  }

  setInputSampleRate(rate: number): void {
    if (rate > 0) this.inputRate = rate;
    this.sendConfig();
  }

  snapshot(): PcmPlaybackStats {
    return {
      ttsFirstByteReceivedMs: this.firstByteAt,
      audiblePlaybackStartedMs: this.audibleAt,
      playbackFinishedMs: this.finishedAt,
      prerollSamples: Math.round((this.prerollMs / 1000) * this.inputRate),
      bufferDepthSamples: this.bufferDepth,
      underrunCount: this.underrunCount,
      underrunDurationMs: Math.round((this.underrunFrames / this.ctx.sampleRate) * 1000),
      overflowCount: this.overflowCount,
      fatalOverflow: this.aborted,
      playedMs: this.getPlayedMs(),
    };
  }

  setPrerollMs(ms: number): void {
    this.prerollMs = Math.min(MAX_PREROLL_MS, Math.max(40, Math.round(ms)));
    this.sendConfig();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : 1;
  }

  async ensureNode(): Promise<void> {
    if (this.node) return;
    if (!this.loadPromise) {
      const base = process.env.PUBLIC_URL || '';
      this.loadPromise = this.ctx.audioWorklet
        .addModule(`${base}/worklets/pcmPlaybackWorklet.js`)
        .then(() => {
          const node = new AudioWorkletNode(this.ctx, 'pcm-playback-processor', {
            numberOfInputs: 0,
            numberOfOutputs: 1,
            outputChannelCount: [1],
          });
          node.port.onmessage = (ev) => this.onWorklet(ev.data);
          const gain = this.ctx.createGain();
          gain.gain.value = this.muted ? 0 : 1;
          node.connect(gain);
          gain.connect(this.ctx.destination);
          this.gain = gain;
          this.node = node;
          this.sendConfig();
        })
        .catch((err) => {
          this.loadPromise = null;
          const message = err instanceof Error ? err.message : 'PCM worklet failed to load';
          this.callbacks.onError?.(message);
          throw err;
        });
    }
    await this.loadPromise;
  }

  beginUtterance(): void {
    this.aborted = false;
    this.playing = false;
    this.odd = null;
    this.pending = [];
    this.firstByteAt = null;
    this.audibleAt = null;
    this.finishedAt = null;
    this.bufferDepth = 0;
    this.underrunCount = 0;
    this.underrunFrames = 0;
    this.overflowCount = 0;
    this.playedInputSamples = 0;
    this.utteranceStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.node?.port.postMessage({ type: 'clear' });
    this.sendConfig();
  }

  pushPcm16(bytes: Uint8Array): void {
    if (this.aborted || bytes.length === 0) return;
    if (this.firstByteAt === null) {
      this.firstByteAt = this.nowMs();
    }
    const merged = this.takeEvenBytes(bytes);
    if (merged.length < 2) return;
    const samples = new Float32Array(merged.length / 2);
    const view = new DataView(merged.buffer, merged.byteOffset, merged.byteLength);
    for (let i = 0; i < samples.length; i += 1) {
      samples[i] = view.getInt16(i * 2, true) / 0x8000;
    }
    if (!this.node) {
      this.pending.push(samples);
      void this.ensureNode().then(() => this.flushPending());
      return;
    }
    this.node.port.postMessage({ type: 'push', samples }, [samples.buffer]);
  }

  flush(): void {
    if (this.aborted) return;
    this.flushPending();
    this.node?.port.postMessage({ type: 'flush' });
  }

  /** Barge-in / next utterance: empty the ring. Do not close the AudioContext or node. */
  clear(): void {
    this.playing = false;
    this.odd = null;
    this.pending = [];
    this.node?.port.postMessage({ type: 'clear' });
  }

  dispose(): void {
    this.aborted = true;
    this.clear();
    try {
      this.node?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      this.gain?.disconnect();
    } catch {
      /* ignore */
    }
    this.node = null;
    this.gain = null;
    this.loadPromise = null;
  }

  private flushPending(): void {
    if (this.aborted || !this.node || this.pending.length === 0) return;
    for (const samples of this.pending) {
      this.node.port.postMessage({ type: 'push', samples }, [samples.buffer]);
    }
    this.pending = [];
  }

  private sendConfig(): void {
    const prerollSamples = Math.round((this.prerollMs / 1000) * this.inputRate);
    this.node?.port.postMessage({
      type: 'config',
      inputSampleRate: this.inputRate,
      prerollSamples,
    });
  }

  private onWorklet(msg: {
    type?: string;
    bufferedSamples?: number;
    count?: number;
    durationFrames?: number;
    playedSamples?: number;
    underrunCount?: number;
    underrunFrames?: number;
    overflowCount?: number;
  }): void {
    if (msg.type === 'stats' && typeof msg.bufferedSamples === 'number') {
      this.bufferDepth = msg.bufferedSamples;
      return;
    }
    if (msg.type === 'started' || msg.type === 'rebuffered') {
      this.playing = true;
      if (this.audibleAt === null) this.audibleAt = this.nowMs();
      if (typeof msg.bufferedSamples === 'number') this.bufferDepth = msg.bufferedSamples;
      this.callbacks.onAudibleStart?.();
      return;
    }
    if (msg.type === 'underrun') {
      this.underrunCount = typeof msg.count === 'number' ? msg.count : this.underrunCount + 1;
      this.underrunFrames += typeof msg.durationFrames === 'number' ? msg.durationFrames : 0;
      this.prerollMs = Math.min(MAX_PREROLL_MS, this.prerollMs + PREROLL_STEP_MS);
      this.playing = false;
      this.sendConfig();
      return;
    }
    if (msg.type === 'overflow') {
      this.overflowCount = typeof msg.count === 'number' ? msg.count : this.overflowCount + 1;
      return;
    }
    if (msg.type === 'fatal_overflow') {
      this.aborted = true;
      this.playing = false;
      this.odd = null;
      this.pending = [];
      this.finishedAt = this.nowMs();
      if (typeof msg.playedSamples === 'number') this.playedInputSamples = msg.playedSamples;
      this.callbacks.onFatalOverflow?.(this.snapshot());
      return;
    }
    if (msg.type === 'ended') {
      this.playing = false;
      this.finishedAt = this.nowMs();
      if (typeof msg.playedSamples === 'number') this.playedInputSamples = msg.playedSamples;
      if (typeof msg.underrunCount === 'number') this.underrunCount = msg.underrunCount;
      if (typeof msg.underrunFrames === 'number') this.underrunFrames = msg.underrunFrames;
      if (typeof msg.overflowCount === 'number') this.overflowCount = msg.overflowCount;
      this.callbacks.onEnded?.(this.snapshot());
    }
  }

  private takeEvenBytes(chunk: Uint8Array): Uint8Array {
    let src = chunk;
    if (this.odd) {
      const joined = new Uint8Array(this.odd.length + chunk.length);
      joined.set(this.odd);
      joined.set(chunk, this.odd.length);
      this.odd = null;
      src = joined;
    }
    if (src.length % 2 === 1) {
      this.odd = src.subarray(src.length - 1);
      return src.subarray(0, src.length - 1);
    }
    return src;
  }

  private nowMs(): number {
    const t = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return Math.round(t - this.utteranceStart);
  }
}
